import * as _ from 'lodash'
import * as React from 'react'
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Card, CardBody, CardFooter, Table } from 'reactstrap';
import { ContentCardHeader } from '../../components/ui/ContentCardHeader';
import { FontAwesomeIcon } from '../../components/ui/FontAwesomeIcon';
import { GridContainer } from '../../components/ui/Grid';
import { SurveyManagementHttp } from './lib/http';
import { IFinalizeSample, ISurveySampleInstance, IVerificationBeforeSend } from './lib/interfaces';

export interface ISurveyVerificationContainerProps extends RouteComponentProps<void> {
  verification?: IVerificationBeforeSend;
}

export interface ISurveyVerificationContainerState {
  isSubmitting: boolean
  wasSubmitted: boolean
  phoneVerification?: IFinalizeSample;
  onlineVerification?: IFinalizeSample;
}

export class SurveyVerificationContainer extends React.Component<ISurveyVerificationContainerProps, {}> {
  state: ISurveyVerificationContainerState = {
    isSubmitting: false,
    wasSubmitted: false
  }

  finalize(action: 'confirm' | 'cancel') {
    const { verification } = this.props

    this.setState({ isSubmitting: true })

    if (!verification) {
      history.back()
    }

    const { batchId = '' } = verification

    const finalizeOnlinePromise = SurveyManagementHttp.finalizeOnlineSample(batchId, action)
      .then((onlineVerification) => this.setState({ onlineVerification }))

    const finalizePhonePromise = SurveyManagementHttp.finalizePhoneSample(batchId, action)
      .then((phoneVerification) => this.setState({ phoneVerification }))

    Promise.all([finalizeOnlinePromise, finalizePhonePromise])
      .finally(() => this.setState({ isSubmitting: false, wasSubmitted: true }))

    if (action === 'cancel') {
      history.back()
    }
  }

  renderFinalizeSample(finalizeSample: IFinalizeSample | undefined, sampleName: string) {
    const { isSubmitting } = this.state
    const doesNotHaveSample = !finalizeSample
    const showSubmittingUI = doesNotHaveSample && isSubmitting
    const message = finalizeSample ? `${finalizeSample.message} for ${sampleName}` : `Submitting ${sampleName}...`
    const iconClassNames = ['fas', 'fa-fw'];

    if (showSubmittingUI) {
      iconClassNames.push('fa-cog fa-spin')
    } else if (finalizeSample) {
      if (finalizeSample.status === 'ok') {
        iconClassNames.push('fa-check-circle text-success');
      } else {
        iconClassNames.push('fa-times-circle text-danger');
      }
    }

    return (
      <h4>
        <i className={iconClassNames.join(' ')} />
        <span>&nbsp;{message}</span>
      </h4>
    )
  }

  render() {
    const { verification } = this.props
    const { isSubmitting, wasSubmitted, phoneVerification, onlineVerification } = this.state

    if (!verification) {
      location.hash = '/clients'
      return
    }

    const { JACSurveySamples, OnlineSamples, batchId, clientId } = this.props.verification
    const allSamples = JACSurveySamples.concat(OnlineSamples)
    const allSurveyNames = _.uniqBy(allSamples, 'surveyName').map((sample) => sample.surveyName)
    const allCategories = _.uniqBy(allSamples, 'category').map((sample) => sample.category)

    const samplesGroupedByCategory = _.groupBy(allSamples, 'category');


    const getSurveyTotalQuantity = (dictionary: _.Dictionary<ISurveySampleInstance[]>, surveyName: string) => {
      let surveys: ISurveySampleInstance[] = [];

      if (surveyName in dictionary) {
        surveys = dictionary[surveyName];
      }

      const reduceAndAdd = (accumulator: number, { quantity }: ISurveySampleInstance) => accumulator + quantity

      const total = surveys.reduce(reduceAndAdd, 0)

      return total.toLocaleString();
    }

    return (
      <section className="blacklight page-bg page-container">
        <GridContainer className="subtleSlideInRight">
          <Card>
            <ContentCardHeader header={
              <header>
                <span>Verify Batch</span>
                <span className="text-muted">&nbsp;#{batchId}</span>
                <span>&nbsp;for</span>
                <span className="text-muted">&nbsp;{clientId}</span>
              </header>
            } />

            {isSubmitting || wasSubmitted ?
              <CardBody className="pt-0">
                <header className="mb-3">
                  {this.renderFinalizeSample(phoneVerification, 'Phone Sample')}
                  {this.renderFinalizeSample(onlineVerification, 'Online Sample')}
                </header>

                <footer>
                  {phoneVerification && phoneVerification.status === 'ok' &&
                    <Button color="success" className="mr-2"
                      onClick={() => SurveyManagementHttp.downloadInterviews(batchId)}>
                      <FontAwesomeIcon icon="download" fixedWidth verticalAlign="unset" />
                      <span>&nbsp;Download Call Center File</span>
                    </Button>
                  }
                  {wasSubmitted &&
                    <Button color="white" href="#/clients">
                      Go Back to Clients List
                    </Button>
                  }
                </footer>
              </CardBody>
              :
              <>
                <Table striped>
                  <thead>
                    <tr>
                      <th></th>
                      {allSurveyNames.map((surveyName, idx) =>
                        <th key={idx}>{surveyName}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {allCategories.map((category, idx) => {
                      const currentCategorySamples = _.groupBy(samplesGroupedByCategory[category], 'surveyName');
                      return (
                        <tr key={idx}>
                          <td>{category}</td>
                          {allSurveyNames.map((surveyName, idx) => {
                            const surveyTotalQuantity = getSurveyTotalQuantity(currentCategorySamples, surveyName);
                            return (
                              <td key={idx}>{surveyTotalQuantity}</td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <CardFooter className="sticky-bottom d-flex justify-content-between bg-white">
                  <Button type="button" color="white" size="lg" className="text-dark mr-2"
                    onClick={() => this.finalize('cancel')}
                    disabled={isSubmitting}>
                    <span>Go Back</span>
                  </Button>
                  <Button type="button" color="secondary" size="lg"
                    onClick={() => this.finalize('confirm')}
                    disabled={isSubmitting}>
                    {isSubmitting &&
                      <FontAwesomeIcon icon="cog" fixedWidth spin verticalAlign="middle" />
                    }
                    <span>Confirm and Process</span>
                  </Button>
                </CardFooter>
              </>
            }
          </Card>
        </GridContainer>
      </section>
    )
  }

}
